import { memo } from 'react';
import styled from '@emotion/styled/macro';
import {
  Checkbox,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from '@sunrun/experience-ui-components';
import {
  filterSelectableReports,
  isTeamGroupNode,
  nodeLabel,
  type HierarchyIndex,
  type PipelineNodeRenderState,
  type SalesRepDisplayMode,
} from 'helpers/pipelineHierarchySelection';
import type { UserHierarchyNode } from 'types/userHierarchy';

function getNodeDisplayLabel(
  node: UserHierarchyNode,
  displayMode: SalesRepDisplayMode,
  isManager: boolean,
  contactCount: number,
): string {
  if (displayMode === 'teams') {
    if (isTeamGroupNode(node)) {
      const teamName = node.User.FullName?.trim() || node.User.Email;
      return `${teamName} (${contactCount})`;
    }
    return node.User.FullName?.trim() || node.User.Email;
  }

  return isManager ? `${nodeLabel(node)} (${contactCount})` : nodeLabel(node);
}

type HierarchyTreeNodeProps = {
  node: UserHierarchyNode;
  depth: number;
  expandedIds: Set<string>;
  onToggleExpand: (nodeId: string) => void;
  checked: boolean;
  canToggle: boolean;
  contactCount: number;
  isManager: boolean;
  onToggleSelection: (nodeEmail: string) => void;
  nodeRenderStates: Map<string, PipelineNodeRenderState>;
  index: HierarchyIndex;
  displayMode: SalesRepDisplayMode;
};

const HierarchyTreeNode = memo(function HierarchyTreeNode({
  node,
  depth,
  expandedIds,
  onToggleExpand,
  checked,
  canToggle,
  contactCount,
  isManager,
  onToggleSelection,
  nodeRenderStates,
  index,
  displayMode,
}: HierarchyTreeNodeProps) {
  const nodeId = node.User.Email;
  if (!index.selectableEmails.has(nodeId)) {
    return null;
  }

  const visibleChildren = filterSelectableReports(node.Reports || [], index);
  const hasChildren = visibleChildren.length > 0;
  const isExpanded = expandedIds.has(nodeId);
  const label = getNodeDisplayLabel(node, displayMode, isManager, contactCount);
  const typographySize = depth < 2 ? 'small' : 'extra_small';
  const labelColor =
    depth < 2 ? tokens.TINTS_OFF_WHITE_10 : tokens.TINTS_OFF_WHITE_20;

  return (
    <TreeRow>
      <RowContent depth={depth}>
        {hasChildren ? (
          <ExpandButton
            type="button"
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            onClick={() => onToggleExpand(nodeId)}>
            <SvgIcon
              name={isExpanded ? SvgNames.ChevronDown : SvgNames.ChevronRight}
              height={16}
              width={16}
            />
          </ExpandButton>
        ) : (
          <ExpandSpacer aria-hidden />
        )}
        <CheckboxWrap>
          <Checkbox
            checked={checked}
            disabled={!canToggle}
            onChange={() => onToggleSelection(nodeId)}
            aria-label={label}
          />
        </CheckboxWrap>
        <RowLabelButton type="button" onClick={() => onToggleSelection(nodeId)}>
          <Typography size={typographySize} color={labelColor}>
            {label}
          </Typography>
        </RowLabelButton>
      </RowContent>
      {hasChildren && isExpanded && (
        <TreeList depth={depth + 1} role="group">
          {visibleChildren.map((child) => {
            const childEmail = child.User.Email;
            const childState = nodeRenderStates.get(childEmail) ?? {
              checked: false,
              canToggle: false,
              contactCount: 0,
              isManager: false,
            };
            return (
              <HierarchyTreeNode
                key={childEmail}
                node={child}
                depth={depth + 1}
                expandedIds={expandedIds}
                onToggleExpand={onToggleExpand}
                checked={childState.checked}
                canToggle={childState.canToggle}
                contactCount={childState.contactCount}
                isManager={childState.isManager}
                onToggleSelection={onToggleSelection}
                nodeRenderStates={nodeRenderStates}
                index={index}
                displayMode={displayMode}
              />
            );
          })}
        </TreeList>
      )}
    </TreeRow>
  );
});

const TreeList = styled.ul<{ depth: number }>`
  list-style: none;
  margin: 0;
  padding: 0;
  padding-left: ${({ depth }) => (depth > 0 ? 12 : 0)}px;
`;

const TreeRow = styled.li`
  margin: 0;
  padding: 0;
`;

const RowContent = styled.div<{ depth: number }>`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 6px 0 6px ${({ depth }) => depth * 12}px;
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  margin-top: 2px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${tokens.TINTS_OFF_WHITE_20};

  &:hover {
    color: ${tokens.TINTS_HERO_BLUE_50};
  }
`;

const ExpandSpacer = styled.span`
  flex-shrink: 0;
  width: 24px;
`;

const CheckboxWrap = styled.div`
  flex-shrink: 0;
  margin-top: 2px;
`;

const RowLabelButton = styled.button`
  flex: 1;
  min-width: 0;
  padding-top: 2px;
  cursor: pointer;
  text-align: left;
  border: none;
  background: transparent;
`;

export { HierarchyTreeNode, TreeList };
